import React, { useState, useEffect } from "react";
import { Button, Space, Tag, Modal, Descriptions, Collapse, Badge, Typography, Tooltip } from "antd";
import { ContrastTag } from "../../../../components/shared";
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined, EyeOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import WorkflowBuilderDrawer from "./components/WorkflowBuilderDrawer";
import PageToolbar from "../../../../components/shared/PageToolbar";
import DataTable from "../../../../components/shared/DataTable";
import StatusTag from "../../../../components/shared/StatusTag";
import { useNotifications } from "../../../../context/NotificationContext";
import { fetchWorkflowTemplates, fetchWorkflowTemplate, deleteWorkflowTemplate, duplicateWorkflowTemplate } from "../../../../api/healthcare/Consultation/ConsultApi";
import { PageContainer } from "../../../../components/shared";

const { Panel } = Collapse;
const { Text } = Typography;

interface WorkflowTemplate {
	id: number;
	name: string;
	description?: string;
	version: number;
	is_active: boolean;
	total_estimated_tokens?: number;
	steps_count?: number;
	created_at: string;
	updated_at: string;
	steps?: any[];
	data_sources?: any[];
	category?: string;
	metadata?: any;
}

const WorkflowsTab: React.FC = () => {
	const [loading, setLoading] = useState(false);
	const [workflows, setWorkflows] = useState<WorkflowTemplate[]>([]);
	const [drawerVisible, setDrawerVisible] = useState(false);
	const [editingWorkflow, setEditingWorkflow] = useState<WorkflowTemplate | null>(null);
	const [previewModalVisible, setPreviewModalVisible] = useState(false);
	const [previewWorkflow, setPreviewWorkflow] = useState<any>(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [search, setSearch] = useState<string>("");
	const [categoryFilter, setCategoryFilter] = useState<string>("");

	const { showSuccess, showError } = useNotifications();

	useEffect(() => {
		loadWorkflows();
	}, []);

	const loadWorkflows = async () => {
		setLoading(true);
		try {
			const response = await fetchWorkflowTemplates();
			const mapped = (response.templates || []).map((w: WorkflowTemplate) => ({
				...w,
				steps_count: w.steps_count || w.steps?.length || 0,
			}));
			setWorkflows(mapped);
		} catch (error) {
			showError("Failed to load workflows");
		} finally {
			setLoading(false);
		}
	};

	const handleCreate = () => {
		setEditingWorkflow(null);
		setDrawerVisible(true);
	};

	const handleEdit = (workflow: WorkflowTemplate) => {
		setEditingWorkflow(workflow);
		setDrawerVisible(true);
	};

	const handlePreview = async (workflow: WorkflowTemplate) => {
		setPreviewModalVisible(true);
		setPreviewLoading(true);
		try {
			const response = await fetchWorkflowTemplate(workflow.id);
			setPreviewWorkflow(response.template || response);
		} catch (error) {
			showError("Failed to load workflow preview");
			setPreviewModalVisible(false);
		} finally {
			setPreviewLoading(false);
		}
	};

	const handleDuplicate = async (workflow: WorkflowTemplate) => {
		try {
			await duplicateWorkflowTemplate(workflow.id);
			showSuccess("Workflow duplicated successfully");
			loadWorkflows();
		} catch (error) {
			showError("Failed to duplicate workflow");
		}
	};

	const handleDelete = async (id: number) => {
		try {
			await deleteWorkflowTemplate(id);
			showSuccess("Workflow deleted successfully");
			loadWorkflows();
		} catch (error) {
			showError("Failed to delete workflow");
		}
	};

	const handleDrawerClose = (shouldRefresh?: boolean) => {
		setDrawerVisible(false);
		setEditingWorkflow(null);
		if (shouldRefresh) loadWorkflows();
	};

	const filteredWorkflows = workflows.filter((w) => {
		const matchesSearch = !search || w.name.toLowerCase().includes(search.toLowerCase());
		const matchesCategory = !categoryFilter || w.category === categoryFilter;
		return matchesSearch && matchesCategory;
	});

	const columns: ColumnsType<WorkflowTemplate> = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (name: string, record: WorkflowTemplate) => (
				<div>
					<Text style={{ fontSize: 13, fontWeight: 500 }}>{name}</Text>
					{record.description && (
						<div>
							<Text type="secondary" style={{ fontSize: 12 }}>
								{record.description.substring(0, 80)}
								{record.description.length > 80 ? "…" : ""}
							</Text>
						</div>
					)}
				</div>
			),
		},
		{
			title: "Ver",
			dataIndex: "version",
			key: "version",
			width: 60,
			render: (version: number) => (
				<ContrastTag background="var(--mg-info)" style={{ fontSize: 11, padding: "0 4px", margin: 0 }}>
					v{version}
				</ContrastTag>
			),
		},
		{
			title: "Steps",
			dataIndex: "steps_count",
			key: "steps_count",
			width: 80,
			render: (count: number) => count || 0,
		},
		{
			title: "Tokens",
			dataIndex: "total_estimated_tokens",
			key: "total_estimated_tokens",
			width: 90,
			render: (tokens: number) => (tokens ? <Text style={{ fontSize: 12 }}>{tokens.toLocaleString()}</Text> : "—"),
		},
		{
			title: "Status",
			dataIndex: "is_active",
			key: "is_active",
			width: 100,
			render: (isActive: boolean) => <StatusTag status={isActive ? "active" : "inactive"} />,
		},
		{
			title: "Updated",
			dataIndex: "updated_at",
			key: "updated_at",
			width: 110,
			render: (date: string) => (date ? <Text style={{ fontSize: 12 }}>{new Date(date).toLocaleDateString()}</Text> : "—"),
		},
		{
			title: "",
			key: "actions",
			width: 130,
			render: (_: any, record: WorkflowTemplate) => (
				<Space size={2}>
					<Tooltip title="Preview workflow">
						<Button
							type="text"
							size="small"
							icon={<EyeOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								handlePreview(record);
							}}
						/>
					</Tooltip>
					<Tooltip title="Edit workflow">
						<Button
							type="text"
							size="small"
							icon={<EditOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								handleEdit(record);
							}}
						/>
					</Tooltip>
					<Tooltip title="Duplicate workflow">
						<Button
							type="text"
							size="small"
							icon={<CopyOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								handleDuplicate(record);
							}}
						/>
					</Tooltip>
					<Tooltip title="Delete workflow">
						<Button
							type="text"
							size="small"
							danger
							icon={<DeleteOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								handleDelete(record.id);
							}}
						/>
					</Tooltip>
				</Space>
			),
		},
	];

	return (
		<PageContainer>
			<PageToolbar
				primaryAction={{
					label: "New Workflow",
					icon: <PlusOutlined />,
					onClick: handleCreate,
				}}
				filters={[
					{
						type: "select",
						placeholder: "All categories",
						options: [
							{ label: "Genetic", value: "genetic" },
							{ label: "Peptide", value: "peptide" },
							{ label: "General", value: "general" },
						],
						value: categoryFilter,
						onChange: setCategoryFilter,
						allowClear: true,
						width: 160,
					},
					{
						type: "search",
						placeholder: "Search workflows…",
						value: search,
						onChange: setSearch,
						width: 260,
					},
				]}
				onReset={() => { setSearch(""); setCategoryFilter(""); }}
			/>

			<DataTable
				title="Consultation Workflows"
				columns={columns}
				dataSource={filteredWorkflows}
				rowKey="id"
				loading={loading}
				onRowClick={(w) => handleEdit(w)}
				emptyTitle="No workflows found"
				emptyDescription="Create your first consultation workflow to get started"
				emptyAction={{ label: "New Workflow", onClick: handleCreate }}
			/>

			<WorkflowBuilderDrawer visible={drawerVisible} workflow={editingWorkflow} onClose={handleDrawerClose} />

			<Modal
				title="Workflow Preview"
				open={previewModalVisible}
				onCancel={() => setPreviewModalVisible(false)}
				width={800}
				footer={[
					<Button key="close" onClick={() => setPreviewModalVisible(false)}>
						Close
					</Button>,
				]}
				destroyOnClose
			>
				{previewLoading ? (
					<div style={{ textAlign: "center", padding: "40px 0" }}>
						<Text type="secondary">Loading…</Text>
					</div>
				) : previewWorkflow ? (
					<div>
						<Descriptions bordered column={1} size="small">
							<Descriptions.Item label="Name">{previewWorkflow.name}</Descriptions.Item>
							<Descriptions.Item label="Description">{previewWorkflow.description || "—"}</Descriptions.Item>
							<Descriptions.Item label="Category">{previewWorkflow.category || "—"}</Descriptions.Item>
							<Descriptions.Item label="Version">v{previewWorkflow.version}</Descriptions.Item>
							<Descriptions.Item label="Status">
								<Badge status={previewWorkflow.is_active ? "success" : "default"} text={previewWorkflow.is_active ? "Active" : "Inactive"} />
							</Descriptions.Item>
							<Descriptions.Item label="Steps">{previewWorkflow.steps?.length || 0}</Descriptions.Item>
							<Descriptions.Item label="Data Sources">{previewWorkflow.data_sources?.length || 0}</Descriptions.Item>
						</Descriptions>

						{previewWorkflow.steps && previewWorkflow.steps.length > 0 && (
							<div style={{ marginTop: 16 }}>
								<Text strong style={{ display: "block", marginBottom: 8 }}>
									Workflow Steps
								</Text>
								<Collapse accordion>
									{previewWorkflow.steps.map((step: any) => (
										<Panel
											header={
												<Space>
													<ContrastTag background="var(--mg-info)">Step {step.step_order}</ContrastTag>
													<Text strong>{step.title}</Text>
													{step.parallel_group && <ContrastTag background="var(--mg-primary)">Parallel</ContrastTag>}
												</Space>
											}
											key={step.id}
										>
											<Descriptions bordered column={1} size="small">
												<Descriptions.Item label="Description">{step.description || "—"}</Descriptions.Item>
												<Descriptions.Item label="Section">{step.section || "—"}</Descriptions.Item>
												<Descriptions.Item label="Time Allocation">{step.time_allocation_minutes || 0} minutes</Descriptions.Item>
												{step.dependencies?.length > 0 && <Descriptions.Item label="Dependencies">{step.dependencies.join(", ")}</Descriptions.Item>}
												<Descriptions.Item label="Prompt Template">
													<pre style={{ fontSize: 11, background: "var(--mg-bg-subtle)", padding: 8, borderRadius: 4, overflow: "auto", maxHeight: 192 }}>{step.prompt_template}</pre>
												</Descriptions.Item>
											</Descriptions>
										</Panel>
									))}
								</Collapse>
							</div>
						)}

						{previewWorkflow.data_sources?.length > 0 && (
							<div style={{ marginTop: 16 }}>
								<Text strong style={{ display: "block", marginBottom: 8 }}>
									Data Sources
								</Text>
								<DataTable
									columns={[
										{ title: "Type", dataIndex: "source_type", key: "source_type", render: (t: string) => <Tag>{t}</Tag> },
										{ title: "Identifier", dataIndex: "source_identifier", key: "source_identifier" },
										{
											title: "Required",
											dataIndex: "is_required",
											key: "is_required",
											render: (req: boolean) => <ContrastTag background={req ? "var(--mg-danger)" : undefined}>{req ? "Required" : "Optional"}</ContrastTag>,
										},
									]}
									dataSource={previewWorkflow.data_sources}
								rowKey={(r: any) => r.id}
									loading={false}
								/>
							</div>
						)}
					</div>
				) : null}
			</Modal>
		</PageContainer>
	);
};

export default WorkflowsTab;
